import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { resolveFileUrl } from '@wix/ricos';
import { useRouter } from '@wix/tpa-router/react';

import type { IFileItem } from 'api/files/types';
import { selectAppData, selectUserToken } from 'store/selectors';
import { formatBytes } from 'common/utils/formatBytes';

import { Link } from 'common/components/Link';

import { More as MoreIcon } from '@wix/wix-ui-icons-common/on-stage';
import { Card } from 'wui/Card';
import { Show } from 'wui/Show';
import { CardHeader } from 'wui/CardHeader';
import { Stack } from 'wui/Stack';
import { Menu, MenuItem } from 'wui/Menu';
import { TextButton } from 'wui/TextButton';
import { IconButton } from 'wui/IconButton';

import { FileIcon } from './FileIcon';
import { useFormattedDate } from './helpers';

interface IFileItemProps {
  item: IFileItem;
}

export function FileItem(props: IFileItemProps) {
  const { item } = props;

  const router = useRouter();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const createdDate = useFormattedDate(item.createdAt);

  const $downloader = React.useRef<HTMLAnchorElement>(null);

  const { metaSiteId } = useSelector(selectAppData);
  const instance = useSelector(selectUserToken);

  const fileUrlResolver = useMemo(
    () => resolveFileUrl(metaSiteId, instance),
    [metaSiteId, instance],
  );

  return (
    <Card>
      <CardHeader
        wired
        verticalAlign="middle"
        avatar={<FileIcon extension={item.type as string} />}
        titleProps={{ noWrap: true, variant: isMobile ? 'p2-14' : 'p2-16' }}
        subtitleProps={{ noWrap: true }}
        title={
          <TextButton as="a" onClick={handleDownloadClick} variant="secondary">
            {item.name}
          </TextButton>
        }
        subtitle={
          <Stack separator={<span>&middot;</span>}>
            <Show if={!!item.size}>
              <span>{formatBytes(item.size)}</span>
            </Show>
            <Link
              state="group.discussion.post"
              params={{ feedItemId: item.feedItemId }}
            >
              {createdDate}
            </Link>
          </Stack>
        }
        action={
          <Menu element={<IconButton icon={<MoreIcon />} />}>
            <MenuItem
              content={t('groups-web.files.actions.download')}
              onClick={handleDownloadClick}
            />
            <MenuItem
              content={t('groups-web.files.actions.view-post')}
              onClick={handleViewPost}
            />
          </Menu>
        }
      />
      {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
      <a
        target="_blank"
        aria-hidden="true"
        ref={$downloader}
        download={item.name}
        style={{ display: 'none' }}
      />
    </Card>
  );

  async function handleDownloadClick() {
    const url = await fileUrlResolver({
      id: item.src?.id as string,
      type: item.type as string,
      privacy: item.src?.private ? 'private' : 'public',
    });

    download(url || undefined);
  }

  function download(fileUrl?: string) {
    if (!$downloader.current || !fileUrl) {
      return;
    }

    $downloader.current.href = fileUrl;
    $downloader.current.click();
  }

  function handleViewPost() {
    router.go('group.discussion.post', { feedItemId: item.feedItemId });
  }
}

FileItem.displayName = 'FileItem';
